import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button, Modal, Form, Table } from "react-bootstrap";
import axios from "axios";
import { API_BASE_URL, reportTakeAction, fetchIndividualReport, reactivateUsers } from "../../../api/endpoint";
import toast from "react-hot-toast";
import { useSelector } from "react-redux";
import Loader from "../../../components/loader";

const ChatReportDetailsPage = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { auth } = useSelector((state) => state.login);
  const [reportDetails, setReportDetails] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [attachmentUrl, setAttachmentUrl] = useState(null);
  const [showActionModal, setShowActionModal] = useState(false);
  const [showReactivateModal, setShowReactivateModal] = useState(false);
  const [actionType, setActionType] = useState(null);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!state?.reportId) return;

    loadDetails();
  }, [state?.reportId]);

  const loadDetails = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${fetchIndividualReport}${state?.reportId}`, {
        headers: { Authorization: `Bearer ${auth}` },
      });
      setReportDetails(res?.data);
      if (Array.isArray(res?.data?.additionalData)) {
        setParticipants(res?.data?.additionalData);
      }

      if (res?.data?.attachmentUrl) {
        setAttachmentUrl(`${API_BASE_URL}public/images/${res?.data?.attachmentUrl}`);
      } else {
        setAttachmentUrl(null);
      }
    } catch (err) {
      console.error("Notification details error:", err);
    } finally {
      setLoading(false);
    }
  };

  const openActionModal = (type, userId) => {
    setActionType({ type, userId });
    setShowActionModal(true);
  };

  const closeActionModal = () => {
    setShowActionModal(false);
    setMessage("");
  };

  const openReactivateModal = (userId) => {
    setActionType({ userId });
    setShowReactivateModal(true);
  };

  const handleActionConfirm = async () => {
    if (!actionType) return;

    const payload = {
      reportId: reportDetails?.reportId,
      reportUserId: actionType?.userId,
      suspend: actionType?.type === "suspend",
      status: "ACTION_TAKEN",
      message: message,
    };

    try {
      setSubmitting(true);
      await axios.post(reportTakeAction, payload, {
        headers: { Authorization: `Bearer ${auth}` },
      });
      toast.success(actionType.type === "suspend" ? "User suspended successfully." : "Warning issued successfully");
      loadDetails();
    } catch (error) {
      console.error("Action error:", error);
      toast.error(error?.response?.data?.user_description);
    } finally {
      setSubmitting(false);
      closeActionModal();
    }
  };

  const reactivateUser = async () => {
    try {
      setSubmitting(true);
      await axios.post(
        reactivateUsers,
        { authIds: [actionType?.userId] },
        { headers: { Authorization: `Bearer ${auth}` } }
      );
      setShowReactivateModal(false);
      toast.success("User reactivated successfully");
      loadDetails();
    } catch (error) {
      console.error("Error updating user status", error);
      toast.error(error?.response?.data?.user_description || "Error reactivating user status");
    } finally {
      setSubmitting(false);
    }
  };

  if (!state?.reportId) {
    return (
      <div className="p-4 text-center">
        <h5>No report selected</h5>
        <Button onClick={() => navigate(-1)}>Go Back</Button>
      </div>
    );
  }

  return (
    <>
      <div className="container py-4">
        <Button variant="secondary" onClick={() => navigate(-1)}>
          ← Back
        </Button>

        <h3 className="mt-3">Chat Report Details</h3>

        <div className="mt-3 p-3 bg-light rounded shadow-sm">
          <p>
            <strong>Reporter:</strong> {reportDetails?.reporterName}
          </p>
          <p>
            <strong>Reason:</strong> {reportDetails?.reason}
          </p>
        </div>

        <div className="mt-4">
          <h5>Participants</h5>

          <Table bordered hover responsive className="mt-2">
            <thead className="table-light">
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Reliability</th>
                <th>Actions</th>
              </tr>
            </thead>

            <tbody>
              {participants.length > 0 ? (
                participants?.map((p, idx) => (
                  <tr key={idx}>
                    <td>
                      {p?.name} {p?.isHost && <span className="text-primary fw-semibold">(Host)</span>}
                    </td>
                    <td>{p?.email}</td>
                    <td>{p?.reliabilityNum ? `${p?.reliabilityNum}%` : "—"}</td>
                    <td className="d-flex gap-2">
                      {p?.suspended ? (
                        <>
                          <span className="text-danger fw-semibold">Suspended</span>
                          <Button variant="success" size="sm" onClick={() => openReactivateModal(p?.id)}>
                            Reactivate
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button variant="warning" size="sm" onClick={() => openActionModal("warn", p?.id)}>
                            Issue Warning
                          </Button>
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => openActionModal("suspend", p?.id)}
                            disabled={submitting}
                          >
                            Suspend
                          </Button>
                        </>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4" className="text-center text-muted">
                    No participants available
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>

        {attachmentUrl && (
          <div className="mt-4">
            <h5>Chat Screenshot</h5>
            <div
              style={{
                maxWidth: "350px",
                background: "#f8f8f8",
                padding: "6px",
                borderRadius: "10px",
              }}
            >
              <img
                src={attachmentUrl}
                alt="attachment"
                style={{
                  width: "100%",
                  height: "auto",
                  objectFit: "contain",
                  borderRadius: "8px",
                }}
              />
            </div>
          </div>
        )}

        <Modal show={showActionModal} onHide={closeActionModal} centered>
          <Modal.Header closeButton>
            <Modal.Title>{actionType?.type === "warn" ? "Issue Warning" : "Suspend User"}</Modal.Title>
          </Modal.Header>

          <Modal.Body>
            <p>
              Are you sure you want to <strong>{actionType?.type === "warn" ? "warn" : "suspend"}</strong> this user?
            </p>

            <Form.Group className="mt-3">
              <Form.Label>Message to User</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="Enter message (optional)"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
              />
            </Form.Group>
          </Modal.Body>

          <Modal.Footer>
            <Button variant="secondary" onClick={closeActionModal} disabled={submitting}>
              Cancel
            </Button>
            <Button
              variant={actionType?.type === "warn" ? "warning" : "danger"}
              onClick={handleActionConfirm}
              disabled={submitting}
            >
              Confirm
            </Button>
          </Modal.Footer>
        </Modal>

        {/* -------- Reactivate User Confirmation Modal -------- */}
        <Modal show={showReactivateModal} onHide={() => setShowReactivateModal(false)} centered>
          <div className="modal-header">
            <h5 className="modal-title">Reactivate User</h5>
            <button className="btn-close" onClick={() => setShowReactivateModal(false)}></button>
          </div>

          <div className="modal-body">
            <p>
              Are you sure you want to <strong>reactivate</strong> this user?
            </p>
          </div>

          <div className="modal-footer">
            <Button variant="secondary" onClick={() => setShowActionModal(false)} disabled={submitting}>
              Cancel
            </Button>

            <Button
              // variant={actionType === "WARN" ? "warning" : "danger"}
              onClick={reactivateUser}
              disabled={submitting}
            >
              Confirm
            </Button>
          </div>
        </Modal>
      </div>
      {/* {loading && <Loader />} */}
    </>
  );
};

export default ChatReportDetailsPage;
